import { API_BASE_URL } from "utils/constants";

// Tells the API which conversation is on screen in this tab, or none, so it skips the push for that conversation
// only (nutivate-api#101). A report lasts 90 seconds, so it's repeated every minute while the app is open. A blank
// report matters too: without reports, the API treats every conversation this app is subscribed to as open.
const HEARTBEAT_MS = 60 * 1000;

let openConversationId = null;
let timer = null;
// The headers of the last report, for one last "none open" report after logout has removed the stored ones.
let lastReport = null;

// One id per page load, so every tab (also a duplicated one) reports its own conversation. A reload's previous id
// was cleared on pagehide.
const deviceId = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const authHeaders = () => {
  try {
    return JSON.parse(localStorage.getItem("authHeaders"));
  } catch (error) {
    return null;
  }
};

const report = ({ closing = false } = {}) => {
  clearTimeout(timer);
  if (!closing) timer = setTimeout(report, HEARTBEAT_MS);

  const stored = authHeaders();
  const signedOut = !stored || !stored.token;
  // After logout, only a last blank report if the previous one said a conversation was open.
  if (signedOut && !(lastReport && lastReport.conversationId)) return;
  const headers = signedOut ? lastReport.headers : stored;

  const conversationId = !signedOut && !closing && document.visibilityState === "visible" ? openConversationId : null;
  lastReport = signedOut ? null : { headers, conversationId };
  // fetch, not the admin panel's request: a failed report must never sign the coach out.
  fetch(`${API_BASE_URL}/coaches/chat_viewing`, {
    method: "PUT",
    // The API names its auth headers token, client, expiry and uid, as stored by the admin panel.
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ device_id: deviceId, conversation_id: conversationId || "" }),
    // Lets the last report out while the tab closes.
    keepalive: closing,
  }).catch(() => {});
};

// Changes arrive in pairs (the old conversation closes, the new one opens), so they're sent together.
const scheduleReport = () => {
  clearTimeout(timer);
  timer = setTimeout(report, 300);
};

export const setOpenConversation = (conversationId) => {
  openConversationId = conversationId;
  scheduleReport();
};

// Only if it's still the open one: React 16 runs the effects of each conversation in list order, so the next
// conversation may already have opened.
export const closeConversation = (conversationId) => {
  if (openConversationId !== conversationId) return;
  openConversationId = null;
  scheduleReport();
};

export const startChatViewing = () => {
  document.addEventListener("visibilitychange", scheduleReport);
  // Closing or leaving the tab tries to clear its report at once (a browser may drop it while the page unloads, and
  // then it expires after 90 seconds). A page restored from the back/forward cache reports again, which also
  // restarts the heartbeat.
  window.addEventListener("pagehide", () => report({ closing: true }));
  window.addEventListener("pageshow", (event) => event.persisted && scheduleReport());
  scheduleReport();
};
